<template>
  <div>
    <div class="vx-col w-full mb-base">
      <vx-card class="w-full">
        <div class="flex items-center justify-between mb-4">
          <div>
            <p class="text-xs mb-1" style="opacity:0.5;text-transform:uppercase;letter-spacing:0.8px;font-weight:600;">YOUTUBE CONTENT ID</p>
            <h4 class="mb-1" style="font-family:Montserrat,sans-serif;font-weight:700;letter-spacing:-0.3px;">허용 목록</h4>
            <p class="text-sm" style="opacity:0.6;margin:0;">Content ID 소유권 주장에서 제외할 채널을 관리합니다.</p>
          </div>
        </div>

        <div class="flex items-center justify-between mb-4" style="gap:16px;">
          <el-input v-model="filter" placeholder="채널 이름 또는 ID 검색" size="small" clearable style="max-width:320px;"></el-input>
          <el-button type="primary" size="small" icon="el-icon-plus" style="background:#7367F0;border-color:#7367F0;" @click="addOpen = true">채널 등록</el-button>
        </div>

        <div v-if="loading" style="padding:32px 0;text-align:center;opacity:0.4;font-size:14px;">불러오는 중…</div>
        <div v-else-if="!filtered.length" style="padding:32px 0;text-align:center;opacity:0.4;font-size:14px;">허용 목록이 비어 있습니다.</div>
        <table v-else class="w-full" style="font-size:13px;border-collapse:collapse;">
          <thead>
            <tr style="border-bottom:2px solid rgba(0,0,0,0.08);">
              <th class="text-left py-2 px-2" style="opacity:0.5;font-weight:600;">채널</th>
              <th class="text-left py-2 px-2" style="opacity:0.5;font-weight:600;">채널 ID</th>
              <th class="text-right py-2 px-2" style="opacity:0.5;font-weight:600;">등록일</th>
              <th class="text-right py-2 px-2" style="opacity:0.5;font-weight:600;">관리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in filtered" :key="c.id" style="border-bottom:1px solid rgba(0,0,0,0.05);">
              <td class="py-3 px-2">
                <a :href="`https://youtube.com/channel/${c.id}`" target="_blank" rel="noopener" style="color:inherit;text-decoration:none;font-weight:500;">
                  {{ c.title || c.name || '(channel)' }} <span style="opacity:0.45;font-size:11px;margin-left:4px;">↗</span>
                </a>
              </td>
              <td class="py-3 px-2"><IdMono :value="c.id" :truncate="22"></IdMono></td>
              <td class="text-right py-3 px-2" style="opacity:0.45;font-size:12px;font-family:monospace;">{{ formatDate(c.timeCreated) }}</td>
              <td class="text-right py-3 px-2">
                <el-button size="mini" plain style="color:#d04444;border-color:rgba(208,68,68,0.4);" @click="askRemove(c)">제거</el-button>
              </td>
            </tr>
          </tbody>
        </table>
      </vx-card>
    </div>

    <el-dialog :visible.sync="addOpen" title="채널 허용 목록 등록" width="520px">
      <div style="display:flex;flex-direction:column;gap:8px;">
        <label style="opacity:0.5;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.8px;">채널 URL 또는 ID</label>
        <el-input v-model="addInput" placeholder="https://youtube.com/channel/UC… 또는 UC…" autofocus></el-input>
        <div v-if="addInput && !parsedChannelId" style="color:#c97a1a;font-size:12px;">
          채널 ID 형식이 올바르지 않습니다. <code style="font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;background:rgba(0,0,0,0.06);padding:0 4px;border-radius:2px;">UC</code>로 시작해야 합니다.
        </div>
      </div>
      <span slot="footer">
        <el-button size="small" @click="addOpen = false">취소</el-button>
        <el-button size="small" type="primary" style="background:#7367F0;border-color:#7367F0;" :loading="adding" :disabled="!parsedChannelId" @click="addChannel">등록</el-button>
      </span>
    </el-dialog>

    <ConfirmActionDialog
      :visible.sync="removeOpen"
      title="허용 목록에서 제거"
      :confirm-token="pending ? pending.id : ''"
      confirm-label="제거"
      :dangerous="true"
      :submitting="removing"
      @confirm="onRemove"
    >
      <template #subject v-if="pending">
        <strong>{{ pending.title || pending.name || pending.id }}</strong> 채널을 허용 목록에서 제거하시겠습니까?
        해당 채널의 동영상이 Content ID 소유권 주장 대상이 됩니다.
      </template>
    </ConfirmActionDialog>
  </div>
</template>

<script>
import { Dialog } from 'element-ui'
import { YoutubeCidApi } from './api.js'
import IdMono from './components/IdMono.vue'
import ConfirmActionDialog from './components/ConfirmActionDialog.vue'
import { toLocalDate } from './dateFormat.js'

const CHANNEL_RE = /UC[A-Za-z0-9_-]{20,}/

export default {
  name: 'YoutubeCidWhitelistsList',
  components: {
    [Dialog.name]: Dialog,
    IdMono,
    ConfirmActionDialog,
  },
  data() {
    return {
      loading: false,
      items: [],
      filter: '',
      addOpen: false,
      addInput: '',
      adding: false,
      removeOpen: false,
      removing: false,
      pending: null,
    }
  },
  computed: {
    filtered() {
      const q = (this.filter || '').trim().toLowerCase()
      if (!q) return this.items
      return this.items.filter((c) =>
        (c.id || '').toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.name || '').toLowerCase().includes(q)
      )
    },
    parsedChannelId() {
      const m = (this.addInput || '').match(CHANNEL_RE)
      return m ? m[0] : ''
    },
  },
  mounted() { this.load() },
  methods: {
    async load() {
      this.loading = true
      try {
        const data = await YoutubeCidApi.listWhitelists()
        this.items = (data && data.items) || []
      } finally {
        this.loading = false
      }
    },
    formatDate(t) {
      if (!t) return '—'
      return toLocalDate(t) || t
    },
    async addChannel() {
      if (!this.parsedChannelId) return
      this.adding = true
      try {
        const created = await YoutubeCidApi.addWhitelist({ id: this.parsedChannelId })
        this.items = [created, ...this.items]
        this.addOpen = false
        this.addInput = ''
      } finally {
        this.adding = false
      }
    },
    askRemove(c) { this.pending = c; this.removeOpen = true },
    async onRemove() {
      if (!this.pending) return
      this.removing = true
      try {
        await YoutubeCidApi.removeWhitelist(this.pending.id)
        this.items = this.items.filter((c) => c.id !== this.pending.id)
        this.removeOpen = false
        this.pending = null
      } finally {
        this.removing = false
      }
    },
  },
}
</script>
